import axios from 'axios';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import { Store } from '../../Store';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { api } from '../../Api';
import StaffPermissionsModal, { PERMISSION_LIST } from '../../component/StaffPermissionsModal';
import '../../assets/ops.css';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        agents: action.payload.products,
        pages: action.payload.pages,
        countProducts: action.payload.countProducts,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

const PERM_LABEL = PERMISSION_LIST.reduce(
  (acc, p) => ({ ...acc, [p.key]: p.label }),
  {}
);

function fmt(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

// A compact row of what this agent is allowed to do: role flags first, then
// each granted capability as its own chip.
function PermChips({ agent }) {
  const roleChips = [
    agent.isUserManager && { label: 'User Mgr', bg: '#e2d9f3', color: '#432874' },
    agent.isProductManager && { label: 'Product Mgr', bg: '#cfe2ff', color: '#084298' },
  ].filter(Boolean);

  const perms = agent.permissions || {};
  const capChips = PERMISSION_LIST.filter((p) => perms[p.key]).map((p) => ({
    label: PERM_LABEL[p.key],
    bg: '#e7f5ff',
    color: '#0b7285',
  }));

  const all = [...roleChips, ...capChips];
  if (all.length === 0) {
    return <span style={{ color: '#adb5bd', fontSize: 12 }}>No permissions yet</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {all.map((c, i) => (
        <span
          key={i}
          style={{
            background: c.bg,
            color: c.color,
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 7px',
            borderRadius: 10,
            whiteSpace: 'nowrap',
          }}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

// Per-agent activity: logins (device/IP), the actions they performed, and any
// live sessions. Pulled from GET /api/users/agents/:id/activity.
function AgentActivityModal({ agent, token, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('logins');

  useEffect(() => {
    let live = true;
    axios
      .get(`${api}/api/users/agents/${agent._id}/activity?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => { if (live) setData(data); })
      .catch((err) => toast.error(getError(err)))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [agent._id, token]);

  const logins = data?.logins || [];
  const actions = data?.actions || [];
  const sessions = data?.sessions || [];

  const TabBtn = ({ id, label, count }) => (
    <button
      className={`ops-btn ${tab === id ? 'primary' : ''}`}
      onClick={() => setTab(id)}
      style={{ fontSize: 13 }}
    >
      {label} ({count})
    </button>
  );

  return (
    <div className="ops-overlay" onClick={onClose}>
      <div className="ops-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="ops-modal-head">
          <div>
            <h4><i className="fa fa-history" style={{ marginRight: 8, color: '#0ea5e9' }} />Agent activity</h4>
            <p className="sub">{agent.name} · @{agent.username}</p>
          </div>
          <button className="ops-x" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        <div className="ops-modal-body">
          {loading ? (
            <div className="ops-empty"><i className="fa fa-spinner fa-spin" />Loading…</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <TabBtn id="logins" label="Logins" count={logins.length} />
                <TabBtn id="actions" label="Actions" count={actions.length} />
                <TabBtn id="sessions" label="Live sessions" count={sessions.length} />
              </div>

              {tab === 'logins' && (
                logins.length === 0 ? <div className="ops-empty">No logins recorded.</div> : (
                  <div className="table-responsive">
                    <table className="table table-sm" style={{ fontSize: 13 }}>
                      <thead><tr><th>When</th><th>Result</th><th>Method</th><th>Device</th><th>IP</th></tr></thead>
                      <tbody>
                        {logins.map((l, i) => (
                          <tr key={i}>
                            <td>{fmt(l.createdAt)}</td>
                            <td>{l.success ? <span style={{ color: '#2b8a3e' }}>OK</span> : <span style={{ color: '#c92a2a' }}>Fail{l.failureReason ? ` · ${l.failureReason}` : ''}</span>}</td>
                            <td>{l.method}{l.role ? ` · ${l.role}` : ''}</td>
                            <td>{[l.brand, l.model, l.platform].filter(Boolean).join(' ') || '—'}</td>
                            <td>{l.ip || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {tab === 'actions' && (
                actions.length === 0 ? <div className="ops-empty">No actions recorded yet. (Actions are captured going forward.)</div> : (
                  <div className="table-responsive">
                    <table className="table table-sm" style={{ fontSize: 13 }}>
                      <thead><tr><th>When</th><th>Action</th><th>Target</th></tr></thead>
                      <tbody>
                        {actions.map((a, i) => (
                          <tr key={i}>
                            <td>{fmt(a.createdAt)}</td>
                            <td>{a.label || a.action}</td>
                            <td>{[a.targetType, a.targetLabel].filter(Boolean).join(' ') || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {tab === 'sessions' && (
                sessions.length === 0 ? <div className="ops-empty">No live sessions.</div> : (
                  <div className="table-responsive">
                    <table className="table table-sm" style={{ fontSize: 13 }}>
                      <thead><tr><th>Since</th><th>Last active</th><th>IP</th><th>Where</th></tr></thead>
                      <tbody>
                        {sessions.map((s, i) => (
                          <tr key={i}>
                            <td>{fmt(s.loginAt)}</td>
                            <td>{fmt(s.lastActive)}</td>
                            <td>{s.ip || '—'}</td>
                            <td>{[s.city, s.country].filter(Boolean).join(', ') || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          )}
        </div>

        <div className="ops-modal-foot">
          <button className="ops-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function AgentListScreen() {
  const [{ loading, agents, pages, countProducts, error }, dispatch] = useReducer(reducer, {
    loading: true,
    agents: [],
    error: '',
  });
  const { state } = useContext(Store);
  const { userInfo } = state;
  const { search } = useLocation();
  const sp = new URLSearchParams(search);
  const page = parseInt(sp.get('page')) || 1;

  const [permAgent, setPermAgent] = useState(null);
  const [activityAgent, setActivityAgent] = useState(null);

  const fetchData = async () => {
    dispatch({ type: 'FETCH_REQUEST' });
    try {
      const { data } = await axios.get(api + `/api/users/agents?page=${page}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      toast.error(getError(err));
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, userInfo]);

  const deleteHandler = async (agent) => {
    if (!window.confirm(`Delete agent ${agent.username}?`)) return;
    try {
      await axios.delete(api + `/api/users/${agent._id}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      toast.success('Agent deleted.');
      fetchData();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <div style={{ padding: '20px 24px' }}>
      <Helmet><title>Manage Agents | ClickCart</title></Helmet>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0 }}>Manage Agents</h2>
          <p style={{ margin: '4px 0 0', color: '#868e96', fontSize: 14 }}>
            {countProducts != null ? `${countProducts} agent${countProducts === 1 ? '' : 's'}` : ' '} · grant permissions to control each agent’s menu
          </p>
        </div>
        <Link to="/admin/addagent" className="btn btn-success">
          <i className="fa fa-plus" style={{ marginRight: 6 }} />Add Agent
        </Link>
      </div>

      {loading ? (
        <LoadingBox />
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <div className="table-responsive" style={{ background: 'white', border: '1px solid #e9ecef', borderRadius: 14, padding: 6 }}>
          <table className="table table-striped align-middle" style={{ marginBottom: 0 }}>
            <thead className="table-light">
              <tr>
                <th>Agent</th>
                <th>Contact</th>
                <th>Permissions</th>
                <th>Last login</th>
                <th>Joined</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent._id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{agent.name || '—'}</div>
                    <div style={{ fontSize: 12, color: '#868e96' }}>@{agent.username}</div>
                  </td>
                  <td style={{ fontSize: 13 }}>
                    <div>{agent.phone || '—'}</div>
                    <div style={{ color: '#868e96' }}>{agent.email || ''}</div>
                  </td>
                  <td style={{ minWidth: 220, maxWidth: 320 }}><PermChips agent={agent} /></td>
                  <td style={{ fontSize: 13 }}>
                    {agent.lastLoginAt ? (
                      <>
                        <div>{fmt(agent.lastLoginAt)}</div>
                        {agent.lastLoginIp && <div style={{ color: '#868e96', fontSize: 11 }}>{agent.lastLoginIp}</div>}
                      </>
                    ) : <span style={{ color: '#adb5bd' }}>Never</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>{fmt(agent.createdAt)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      title="Roles & permissions"
                      onClick={() => setPermAgent(agent)}
                      style={{ marginRight: 6 }}
                    >
                      <i className="fa fa-shield" />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-info"
                      title="Activity"
                      onClick={() => setActivityAgent(agent)}
                      style={{ marginRight: 6 }}
                    >
                      <i className="fa fa-history" />
                    </button>
                    <Link
                      to={`/admin/edituser/${agent._id}`}
                      className="btn btn-sm btn-outline-secondary"
                      title="Edit profile"
                      style={{ marginRight: 6 }}
                    >
                      <i className="fa fa-pencil" />
                    </Link>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      title="Delete"
                      onClick={() => deleteHandler(agent)}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
              {agents.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#868e96', padding: 24 }}>No agents yet. Use “Add Agent”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <nav style={{ marginTop: 16 }}>
          <ul className="pagination justify-content-center">
            {[...Array(pages).keys()].map((x) => (
              <li key={x + 1} className={`page-item ${x + 1 === page ? 'active' : ''}`}>
                <Link className="page-link" to={`/admin/agents?page=${x + 1}`}>{x + 1}</Link>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {permAgent && (
        <StaffPermissionsModal
          user={permAgent}
          token={userInfo.token}
          onClose={() => setPermAgent(null)}
          onSaved={fetchData}
        />
      )}
      {activityAgent && (
        <AgentActivityModal
          agent={activityAgent}
          token={userInfo.token}
          onClose={() => setActivityAgent(null)}
        />
      )}
    </div>
  );
}
